/**
 * What the assistant can see.
 *
 * A grounded answer that quotes "412 unlabeled images in line-scan-v2" is only
 * trustworthy if the user can check where that came from. This panel is that
 * receipt: the same projects, models, versions and counts the backend folds
 * into its prompt, with a per-row button that asks about that exact version.
 *
 * It is also the honest place to turn grounding OFF. Sending a workspace
 * summary with every question is a privacy-relevant default, so the switch is
 * here next to the data rather than buried in a settings screen.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../i18n';
import Badge from '../../../components/Atoms/Badge';
import { flattenVersions, summariseWorkspace } from '../utils/suggestions';

const TYPE_LABEL_KEY = {
  classification: 'assistant.modelType.classification',
  bounding_box: 'assistant.modelType.detection',
  segmentation: 'assistant.modelType.segmentation',
};

const STATUS_VARIANT = {
  completed: 'success',
  inprogress: 'info',
  queued: 'warning',
  failed: 'danger',
  pending: 'neutral',
};

/**
 * `trainingStatus` is a free-text field on the version doc, so an unexpected
 * value must not reach `t()` — a missing key renders the key itself, and the
 * user would read "assistant.trainingStatus.halted" in a badge.
 */
const normaliseStatus = (raw) => {
  const key = (raw || 'pending').replace(/[\s_-]/g, '').toLowerCase();
  return Object.prototype.hasOwnProperty.call(STATUS_VARIANT, key) ? key : 'pending';
};

const WorkspaceContextPanel = ({
  workspace,
  loading,
  includeContext,
  onToggleContext,
  onRefresh,
  onAsk,
  onClose,
}) => {
  const { t } = useI18n();
  const navigate = useNavigate();

  const totals = summariseWorkspace(workspace);
  const rows = flattenVersions(workspace);

  return (
    <aside className="lf-context" aria-label={t('assistant.context.heading')}>
      <header className="lf-context__head">
        <h2 className="lf-context__title">
          <i className="fa fa-database" aria-hidden="true" /> {t('assistant.context.heading')}
        </h2>
        <div className="lf-context__head-tools">
          <button
            type="button"
            className="lf-context__tool"
            onClick={onRefresh}
            disabled={loading}
            title={t('common.refresh')}
            aria-label={t('common.refresh')}
          >
            <i className={`fa fa-sync${loading ? ' fa-spin' : ''}`} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="lf-context__tool"
            onClick={onClose}
            title={t('common.close')}
            aria-label={t('common.close')}
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </div>
      </header>

      <label className="lf-context__switch">
        <input
          type="checkbox"
          checked={!!includeContext}
          onChange={(event) => onToggleContext?.(event.target.checked)}
        />
        <span>
          <span className="lf-context__switch-label">{t('assistant.context.useLabel')}</span>
          <span className="lf-context__switch-hint">{t('assistant.context.useHint')}</span>
        </span>
      </label>

      <dl className="lf-context__stats">
        <div>
          <dt>{t('assistant.context.projects')}</dt>
          <dd>{totals.projects}</dd>
        </div>
        <div>
          <dt>{t('assistant.context.models')}</dt>
          <dd>{totals.models}</dd>
        </div>
        <div>
          <dt>{t('assistant.context.images')}</dt>
          <dd>{totals.images.toLocaleString()}</dd>
        </div>
        <div>
          <dt>{t('assistant.context.unlabeled')}</dt>
          <dd className={totals.unlabeled ? 'lf-context__warn' : undefined}>
            {totals.unlabeled.toLocaleString()}
          </dd>
        </div>
      </dl>

      <div className="lf-context__list">
        {rows.length === 0 ? (
          <div className="lf-context__none">
            <p>{t('assistant.context.none')}</p>
            <button
              type="button"
              className="lf-context__cta"
              onClick={() => navigate('/ProjectManagement')}
            >
              <i className="fa fa-folder" aria-hidden="true" /> {t('assistant.quick.projects')}
            </button>
          </div>
        ) : (
          rows.map((row, index) => {
            const status = normaliseStatus(row.trainingStatus);
            return (
              <article
                className="lf-context__row"
                key={`${row.projectName}-${row.modelName}-${row.version}-${index}`}
              >
                <div className="lf-context__row-head">
                  <span className="lf-context__row-name" title={`${row.projectName} / ${row.modelName}`}>
                    {row.modelName}
                  </span>
                  <span className="lf-context__row-version">v{row.version}</span>
                </div>
                <div className="lf-context__row-tags">
                  <Badge variant="neutral">
                    {t(TYPE_LABEL_KEY[row.modelType] || 'assistant.modelType.unknown')}
                  </Badge>
                  <Badge variant={STATUS_VARIANT[status]}>
                    {t(`assistant.trainingStatus.${status}`)}
                  </Badge>
                </div>
                <p className="lf-context__row-meta">
                  {t('assistant.context.rowMeta', {
                    total: row.total.toLocaleString(),
                    labeled: row.labeled.toLocaleString(),
                    classes: row.classes,
                  })}
                </p>
                {row.unlabeled > 0 && (
                  <p className="lf-context__row-warn">
                    <i className="fa fa-tag" aria-hidden="true" />{' '}
                    {t('assistant.context.rowUnlabeled', { count: row.unlabeled.toLocaleString() })}
                  </p>
                )}
                <button
                  type="button"
                  className="lf-context__row-ask"
                  onClick={() =>
                    onAsk?.(
                      t('assistant.context.askPrompt', {
                        model: row.modelName,
                        version: row.version,
                      }),
                    )
                  }
                >
                  <i className="fa fa-comment" aria-hidden="true" /> {t('assistant.context.ask')}
                </button>
              </article>
            );
          })
        )}
      </div>
    </aside>
  );
};

export default WorkspaceContextPanel;
